import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CordeCommonModelsDtoMachineMachine } from '@/api/generated/v1/api';

export interface MachineStatusStreamConfig {
  statusFilter?: string;
  categoryFilter?: string;
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
}

export interface MachineStreamStatus {
  connected: boolean;
  error: string | null;
  reconnectAttempts: number;
}

export interface MachineListResponse {
  machines: CordeCommonModelsDtoMachineMachine[];
  pagination?: any;
}

export const useMachineStatusStream = (config: MachineStatusStreamConfig = {}) => {
  console.log('[useMachineStatusStream] Hook initialized with config:', config);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [machines, setMachines] = useState<CordeCommonModelsDtoMachineMachine[]>([]);
  const [status, setStatus] = useState<MachineStreamStatus>({
    connected: false,
    error: null,
    reconnectAttempts: 0
  });

  const { accessToken, refreshToken } = useAuth();
  console.log('[useMachineStatusStream] Auth state:', { hasAccessToken: !!accessToken });
  
  const {
    statusFilter,
    categoryFilter,
    autoReconnect = true,
    maxReconnectAttempts = 5,
    reconnectDelay = 3000
  } = config;

  // Build WebSocket URL with filters
  const buildWebSocketUrl = useCallback(() => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    
    let wsHost;
    // For localhost development, use port 8080
    if (window.location.hostname === 'localhost') {
      wsHost = window.location.hostname;
      const wsPort = ':8080';
      wsHost += wsPort;
    } else {
      // For deployed environments, use API host instead of GUI host
      if (window.location.hostname === 'casino-gui-dev.corde.tech') {
        wsHost = 'casino-api-dev.corde.tech';
      } else if (window.location.hostname === 'casino-gui.corde.tech') {
        wsHost = 'casino-api.corde.tech';
      } else {
        // Fallback to current host for other environments
        wsHost = window.location.hostname;
      }
    }
    
    const params = new URLSearchParams();
    if (accessToken) {
      params.append('access_token', accessToken);
    }
    if (statusFilter) {
      params.append('status', statusFilter);
    }
    if (categoryFilter) {
      params.append('category', categoryFilter);
    }
    
    // Build the WebSocket URL
    const wsUrl = `${wsProtocol}//${wsHost}/ws/v1/machine/status-stream?${params.toString()}`;
    
    console.log('[buildWebSocketUrl] Generated WebSocket URL:', wsUrl);
    return wsUrl;
  }, [accessToken, statusFilter, categoryFilter]);

  // Connect to WebSocket with token refresh support
  const connectWebSocket = useCallback(async () => {
    if (!accessToken) {
      console.warn('No access token available for machine status stream');
      return;
    }

    const wsUrl = buildWebSocketUrl();
    console.log('Connecting to machine status stream:', wsUrl);
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('Machine status WebSocket connected');
        setStatus(prev => ({
          ...prev,
          connected: true,
          error: null,
          reconnectAttempts: 0
        }));
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Received WebSocket message:', data);
          
          // Check if it's a status update message
          if (data.type === 'status_update' && data.payload) {
            console.log('Processing status update:', data.payload);
            
            // Update the specific machine in the list
            setMachines(prevMachines => {
              const updatedMachines = prevMachines.map(machine => {
                if (machine.id === data.payload.machine_id) {
                  // Update the machine status
                  return {
                    ...machine,
                    status: data.payload.new_status,
                    // Update any other fields that might have changed
                    name: data.payload.name || machine.name,
                    category: data.payload.category || machine.category,
                  };
                }
                return machine;
              });
              
              console.log('Updated machines list:', updatedMachines);
              return updatedMachines;
            });
          } 
          // Handle initial data load (full machine list)
          else if (data.machines) {
            console.log('Received initial machine list:', data);
            
            if (Array.isArray(data.machines)) {
              console.log('Setting machines state:', data.machines);
              setMachines(data.machines);
            } else if (data.machines && typeof data.machines === 'object' && 'machines' in data.machines) {
              // Handle nested structure
              const machinesArray = (data.machines as any).machines;
              console.log('Found nested machines array:', machinesArray);
              if (Array.isArray(machinesArray)) {
                setMachines(machinesArray);
              }
            }
          }
        } catch (error) {
          console.error('Failed to parse machine status message:', error);
          setStatus(prev => ({ ...prev, error: 'Failed to parse status update' }));
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('Machine status WebSocket error:', error);
        setStatus(prev => ({ ...prev, error: 'WebSocket connection error' }));
      };
      
      wsRef.current.onclose = async (event) => {
        console.log('Machine status WebSocket closed:', event.code, event.reason);
        
        // Check if close was due to authentication error (code 1011 or reason containing "token")
        const isAuthError = event.code === 1011 || 
                           (event.reason && event.reason.toLowerCase().includes('token')) ||
                           (event.reason && event.reason.toLowerCase().includes('unauthorized'));
        
        setStatus(currentStatus => {
          const newStatus = { ...currentStatus, connected: false };
          
          // If it's an auth error, try to refresh token first
          if (isAuthError && currentStatus.reconnectAttempts === 0) {
            console.log('Attempting token refresh due to auth error');
            refreshToken().then((newToken) => {
              if (newToken) {
                console.log('Token refreshed, attempting reconnection');
                // Give a small delay before reconnecting with new token
                setTimeout(() => connectWebSocket(), 1000);
              } else {
                console.error('Token refresh failed');
                setStatus(prev => ({ 
                  ...prev, 
                  error: 'Authentication failed - please login again' 
                }));
              }
            });
            return { ...newStatus, reconnectAttempts: 1 };
          }
          
          // Auto-reconnect if enabled and not too many attempts
          if (autoReconnect && currentStatus.reconnectAttempts < maxReconnectAttempts) {
            newStatus.reconnectAttempts = currentStatus.reconnectAttempts + 1;
            
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log(`Attempting to reconnect machine status stream (${newStatus.reconnectAttempts}/${maxReconnectAttempts})`);
              connectWebSocket();
            }, reconnectDelay * newStatus.reconnectAttempts);
          }
          
          return newStatus;
        });
      };
      
    } catch (error) {
      console.error('Failed to create machine status WebSocket:', error);
      setStatus(prev => ({ ...prev, error: 'Failed to create WebSocket connection' }));
    }
  }, [buildWebSocketUrl, accessToken, refreshToken, autoReconnect, maxReconnectAttempts, reconnectDelay]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    console.log('Disconnecting machine status stream');
    
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setStatus({
      connected: false,
      error: null,
      reconnectAttempts: 0
    });
  }, []);

  // Manual reconnect
  const reconnect = useCallback(async () => {
    disconnect();
    setTimeout(() => {
      connectWebSocket();
    }, 100);
  }, [disconnect, connectWebSocket]);

  // Connect when component mounts or token/filters change
  useEffect(() => {
    console.log('[useMachineStatusStream] useEffect triggered:', {
      hasAccessToken: !!accessToken,
      statusFilter,
      categoryFilter
    });
    
    if (accessToken) {
      console.log('[useMachineStatusStream] Access token present, connecting WebSocket...');
      connectWebSocket();
    } else {
      console.log('[useMachineStatusStream] No access token, skipping WebSocket connection');
    }
    
    // Cleanup on unmount or dependency change
    return () => {
      console.log('[useMachineStatusStream] Cleanup: disconnecting WebSocket');
      disconnect();
    };
  }, [accessToken, statusFilter, categoryFilter]);

  return {
    machines,
    status,
    disconnect,
    reconnect
  };
};